import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { monoColumn, mutedColumn, statusColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, listPager, usePagedRows } from 'library/components/ListScreen';
import { ClosePeriod } from 'constants/actions';
import { FinancialPeriod as FinancialPeriodResource } from 'constants/resources';
import localeTranslator from 'library/Hocs/LocalTranslator';
import services from 'services';
var FinancialPeriods = function (props) {
    var t = props.translate;
    var financialPeriodService = services.financialPeriodService, permissionService = services.permissionService, userService = services.userService;
    var _a = financialPeriodService.useFinancialPeriods(), financialPeriods = _a.data, isLoading = _a.isLoading;
    var openNewFinancialPeriod = financialPeriodService.useOpenFinancialPeriod().mutate;
    var closeOpeningFinancialPeriod = financialPeriodService.useCloseFinancialPeriod().mutate;
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var debounceRef = useRef(undefined);
    var canOpen = permissionService.hasPermissionToAdd(FinancialPeriodResource);
    var canClose = userService.hasPermission(ClosePeriod, FinancialPeriodResource);
    var openedPeriod = financialPeriods === null || financialPeriods === void 0 ? void 0 : financialPeriods.find(function (period) { return period.getIsOpen(); });
    var canManage = openedPeriod ? canClose : canOpen;
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        return (financialPeriods || []).filter(function (period) {
            if (!query)
                return true;
            var status = t(period.getIsOpen() ? 'open' : 'closed');
            return [period.getFormattedFromDate(), period.getFormattedToDate(), status, String(period.getId())]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });
    }, [financialPeriods, searchValue, t]);
    var _c = usePagedRows(filtered), page = _c.page, setPage = _c.setPage, pages = _c.pages, from = _c.from, to = _c.to, pageRows = _c.pageRows;
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var onPeriodAction = function () {
        if (openedPeriod) {
            closeOpeningFinancialPeriod({
                id: openedPeriod.getId(),
                successMessage: t('messages.financialPeriodClosed')
            });
            return;
        }
        openNewFinancialPeriod({
            successMessage: t('messages.financialPeriodOpened')
        });
    };
    var columns = useMemo(function () { return [
        monoColumn({ key: 'id', title: t('id'), width: '88px', value: function (period) { return period.getId(); } }),
        mutedColumn({ key: 'from', title: t('fromDate'), width: 'minmax(140px, 1fr)', value: function (period) { return period.getFormattedFromDate(); } }),
        mutedColumn({ key: 'to', title: t('toDate'), width: 'minmax(140px, 1fr)', value: function (period) { return period.getFormattedToDate(); } }),
        statusColumn({
            title: t('active'),
            width: '140px',
            active: function (period) { return period.getIsOpen(); },
            activeLabel: t('open'),
            inactiveLabel: t('closed')
        })
    ]; }, [t]);
    return (_jsx(ListScreen, { title: t('header.financialPeriods'), count: t('countBadge', { count: (financialPeriods === null || financialPeriods === void 0 ? void 0 : financialPeriods.length) || 0 }), subtitle: t('subtitle'), actions: canManage && !isLoading ? (_jsx(ListAddButton, { onboarding: true, icon: null, label: openedPeriod ? t('closeFinancialPeriod') : t('openFinancialPeriod'), onClick: onPeriodAction })) : undefined, search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, children: _jsx(DataTable, { minWidth: "640px", loading: isLoading, rows: pageRows, rowKey: function (period) { return period.getId(); }, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                total: filtered.length,
                page: page,
                pages: pages,
                meta: t('showingRange', { from: from, to: to, total: filtered.length }),
                previous: t('previous'),
                next: t('next'),
                center: t('pageOf', { page: page, pages: pages }),
                setPage: setPage
            }) }) }));
};
export default localeTranslator(FinancialPeriods, 'financialPeriods');
